import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Wrapper from "./Wrapper";
import HamburgerMenu from "./HamburgerMenu";
import Account from "../user/Account";
import logo from "../../../assets/images/logo.svg";
import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../constants/routes";

// Matches the header's min-h-[80px], so the page doesn't jump when it becomes fixed
const HEADER_HEIGHT_CLASS = "h-[80px]";

export interface NavItem {
    label: string;
    to: string;
}

const Header = () => {
    const { lang, t } = useLanguage();
    const headerRef = useRef<HTMLElement>(null);
    const [isSticky, setIsSticky] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const header = headerRef.current;
            if (!header) return;

            // Header becomes fixed once the user scrolls past its own height
            setIsSticky(window.scrollY > header.offsetHeight);
        };

        handleScroll(); // Sync once on mount, in case the page loads already scrolled
        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Links follow the active language instead of being hardcoded to /fa
    const navItems: NavItem[] = [
        { label: t.nav.blog, to: ROUTES.blog(lang) },
        { label: t.nav.courses, to: ROUTES.courses(lang) }
    ];

    return (
        <>
            <header
                ref={headerRef}
                className={`w-full py-1 z-50 transition-all min-h-[80px] backdrop-blur-sm duration-300 border-b-4 border-gray-200 ${
                    isSticky ? "fixed top-0 left-0 bg-white/70 shadow-md" : "relative"
                }`}
            >
                <Wrapper className="flex relative items-center">
                    <Link
                        to={ROUTES.home(lang)}
                        className="absolute left-1/2 top-1/2 lg:top-0 lg:translate-y-0 -translate-x-1/2 flex items-center gap-x-1 md:static md:translate-x-0 md:mx-0 md:gap-x-2 order-2 md:order-1"
                    >
                        {/* Empty alt: the brand name is already next to it as text */}
                        <img src={logo} alt="" className="h-14 rounded-sm" />
                        <span className="text-2xl font-bold text-light-primary">
                            {t.nav.brand}
                        </span>
                    </Link>

                    {/* Hamburger menu: right on small screens */}
                    <HamburgerMenu
                        navItems={navItems}
                        className="inline-block order-1 lg:order-2"
                    />

                    <Account className="order-3" />
                </Wrapper>
            </header>

            {/* Fills the space left behind when the header becomes fixed */}
            {isSticky && <div className={HEADER_HEIGHT_CLASS} aria-hidden="true" />}
        </>
    );
};

export default Header;